import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../utils/tpoGenQueryKeys';
import { sendNotification } from 'utils/helperMethods';
import { TPOGEN_PROMOTION_DELETED } from 'utils/mixpanelEventNames';
import { useEditWriteGuard } from '../../EditSession/useEditWriteGuard';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../../../utils/trackTpoGenEvent';

const deletePromo = async (promotionId: string) => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.deletePromo(promotionId), {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    return response.data;
};

const useDeletePromoMutation = ({
    eventId,
    onSuccess,
}: {
    eventId: string;
    onSuccess?: (deletedPromoId: string) => void;
} = { eventId: '' }) => {
    const queryClient = useQueryClient();
    const assertCanWrite = useEditWriteGuard();

    const { mutate, isPending } = useMutation({
        mutationFn: (promotionId: string) => {
            assertCanWrite();
            return deletePromo(promotionId);
        },
        onSuccess: (_data, promotionId) => {
            // Runs before the cache work so the caller can close any tab bound to this
            // promo first. removeQueries only evicts the entry — an observer still
            // mounted when it re-renders would rebuild the query and GET a promo the
            // server just deleted, so the tab has to be on its way out by this point.
            onSuccess?.(promotionId);

            queryClient.removeQueries({ queryKey: tpoGenKeys.promoDetails(promotionId) });
            queryClient.invalidateQueries({ queryKey: tpoGenKeys.all });
            if (eventId) {
                queryClient.invalidateQueries({ queryKey: tpoGenKeys.eventDetails(eventId) });
            }
            queryClient.invalidateQueries({ queryKey: ['calendarData'] });
            sendNotification('success', 'Promo deleted successfully');
            trackTpoGenEvent(TPOGEN_PROMOTION_DELETED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_EVENTS, {
                event_id: eventId,
                promotion_id: promotionId,
            });
        },
        onError: (error: any) => {
            const message = error?.response?.data?.message || error?.message || 'Failed to delete promo';
            sendNotification('error', message);
        },
    });

    return { mutate, isPending };
};

export default useDeletePromoMutation;
